// src/pages/admin/GastosPage.tsx
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import BarcodeScannerModal from "../../components/BarcodeScannerModal";
import {
  getOrdersNormalized,
  getBanks,
  getSuppliers,
  createExpense,
  formatAxiosError,
  type Banco,
  type Proveedor,
  type OC,
  getProductTypes,
  ProductType,
  getProducts,
  Product,
  PurchaseCategory,
  getPurchaseCategories
} from "../../services/api";
//import "./GastosPage.module.css";

type MsgType = { t: "s" | "e"; text: string } | null;

const GastosPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const odeSIdFromUrl = searchParams.get("odeSId");

  const [orderDetailId, setOrderDetailId] = useState<string>("");
  const [orderId, setOrderId] = useState(odeSIdFromUrl ?? "");
  const [orders, setOrders] = useState<OC[]>([]);
  const [banks, setBanks] = useState<Banco[]>([]);
  const [suppliers, setSuppliers] = useState<Proveedor[]>([]);
  const [productTypes, setProductTypes] = useState<ProductType[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [purchaseCategories, setPurchaseCategories] = useState<PurchaseCategory[]>([]);

  const [bankId, setBankId] = useState("");
  const [supplierId, setSupplierId] = useState("");
  const [amountSpent, setAmountSpent] = useState("");
  const [code, setCode] = useState("");
  const [codeBar, setCodeBar] = useState("");
  const [productTypeId, setProductTypeId] = useState("");
  const [productId, setProductId] = useState("");
  const [quantity, setQuantity] = useState("");
  const [unitPrice, setUnitPrice] = useState("");
  const [purchaseCategoryId, setPurchaseCategoryId] = useState("");

  const [receiptFile, setReceiptFile] = useState<File | null>(null);
  const [evidenceFile, setEvidenceFile] = useState<File | null>(null);
  const [invoicePdf, setInvoicePdf] = useState<File | null>(null);
  const [invoiceXml, setInvoiceXml] = useState<File | null>(null);

  const [loading, setLoading] = useState(false);
  const [loadingCatalogs, setLoadingCatalogs] = useState(true);
  const [msg, setMsg] = useState<MsgType>(null);
  const [isOpen, setIsOpen] = useState<boolean>(false);

  useEffect(() => {
    setOrderDetailId("");
  }, [orderId]);

  useEffect(() => {
    const pt = selectedOrder?.items.find(item => item.id+'' === orderDetailId);
    //console.log('GastosPage - orderDetailId', pt);
    setProductTypeId(pt?.productTypeId+'');
    setSupplierId(pt?.supplierId+'');
    setQuantity(pt?.cantidad+'');
    setUnitPrice(pt?.precioUnitario+'');
  }, [orderDetailId]);

  useEffect(() => {
    const fetchCatalogos = async () => {
      setLoadingCatalogs(true);
      try {
        const [ord, bnk, sup, prodTypes, purchCategories] = await Promise.all([
          getOrdersNormalized(),
          getBanks(),
          getSuppliers(),
          getProductTypes(),
          getPurchaseCategories(),
        ]);

        setOrders(ord);
        setBanks(bnk);
        setSuppliers(sup);
        setProductTypes(prodTypes);
        setPurchaseCategories(purchCategories);
      } catch (err) {
        const flat = formatAxiosError(err);
        console.error("Error cargando catálogos", err);
        setMsg({ t: "e", text: `❌ Error cargando catálogos: ${flat.message}` });
      } finally {
        setLoadingCatalogs(false);
      }
    };

    fetchCatalogos();
  }, []);

  useEffect(() => {
      const pt = selectedOrder?.items.find(item => item.barcode+'' === codeBar);
      //console.log('GastosPage - codeBar', codeBar, pt);
      setOrderDetailId(pt?.id+'');
  }, [codeBar]);

  useEffect(() => {
    const fetchProducts = async () => {
      if (productTypeId === undefined) return;
      setLoadingCatalogs(true);
      try {
        const [products] = await Promise.all([
          getProducts(productTypeId),
        ]);
        
        setProducts(products);

        let pt = selectedOrder?.items.find(item => item.id+'' === orderDetailId && item.productTypeId+'' === productTypeId);
        if (pt) {
          //console.log('GastosPage - getProducts', pt);
        }
        else if (codeBar !== ''){
          pt = selectedOrder?.items.find(item => item.barcode+'' === codeBar && item.productTypeId+'' === productTypeId);
          //console.log('GastosPage - barcode', pt);
        }
        setProductId(pt?.productId+'');
        setQuantity(pt?.cantidad+'');
        setUnitPrice(pt?.precioUnitario+'')
        
      } catch (err) {
        const flat = formatAxiosError(err);
        console.error("Error cargando productos", err);
        setMsg({ t: "e", text: `❌ Error cargando productos: ${flat.message}` });
      } finally {
        setLoadingCatalogs(false);
      }
    };

    if (productTypeId && productTypeId !== 'undefined') fetchProducts();
  }, [productTypeId]);

  const selectedOrder = useMemo(
    () => orders.find((o) => String(o.id) === String(orderId)),
    [orders, orderId]
  );

  const parsedAmount = useMemo(() => {
    const n = parseFloat(amountSpent);
    return Number.isFinite(n) ? n : 0;
  }, [amountSpent]);

  const remainingAfterExpense = useMemo(() => {
    if (!selectedOrder) return null;
    return selectedOrder.presupuestoTotal - selectedOrder.totalEstimado - parsedAmount;
  }, [selectedOrder, parsedAmount]);

  const clearFiles = () => {
    setReceiptFile(null);
    setEvidenceFile(null);
    setInvoicePdf(null);
    setInvoiceXml(null);
  };

  const clearFormButKeepOrder = () => {
    setOrderDetailId("");
    setBankId("");
    setSupplierId("");
    setAmountSpent("");
    setCode("");
    clearFiles();
  };

  const validateFile = (
    file: File | null,
    allowedExt: string[],
    maxSizeMb = 10
  ): string | null => {
    if (!file) return null;

    const ext = "." + (file.name.split(".").pop()?.toLowerCase() || "");
    if (!allowedExt.includes(ext)) {
      return `El archivo "${file.name}" no tiene un formato permitido (${allowedExt.join(", ")})`;
    }

    const maxBytes = maxSizeMb * 1024 * 1024;
    if (file.size > maxBytes) {
      return `El archivo "${file.name}" supera el límite de ${maxSizeMb} MB`;
    }

    return null;
  };

  const fileToBase64 = (file: File): Promise<string> =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        const result = String(reader.result || "");
        const base64 = result.includes(",") ? result.split(",")[1] : result;
        resolve(base64);
      };
      reader.onerror = () =>
        reject(new Error(`No se pudo leer el archivo ${file.name}`));
    });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setMsg(null);

    if (!orderId) {
      setMsg({ t: "e", text: "❌ Selecciona una Orden de Compra" });
      return;
    }

    if (!bankId) {
      setMsg({ t: "e", text: "❌ Selecciona un Banco / Caja" });
      return;
    }

    if (!supplierId) {
      setMsg({ t: "e", text: "❌ Selecciona un proveedor" });
      return;
    }

    if (!amountSpent || parsedAmount <= 0) {
      setMsg({ t: "e", text: "❌ Ingresa un monto válido mayor a 0" });
      return;
    }

    if (!code.trim()) {
      setMsg({ t: "e", text: "❌ Ingresa el número de ticket o folio" });
      return;
    }

    const fileError =
      validateFile(receiptFile, [".pdf", ".jpg", ".jpeg", ".png", ".webp"]) ||
      validateFile(evidenceFile, [".pdf", ".jpg", ".jpeg", ".png", ".webp"]) ||
      validateFile(invoicePdf, [".pdf"]) ||
      validateFile(invoiceXml, [".xml"]);

    if (fileError) {
      setMsg({ t: "e", text: `❌ ${fileError}` });
      return;
    }

    setLoading(true);

    try {
      let receiptBase64: string | undefined;
      let evidenceBase64: string | undefined;
      let invoicePdfBase64: string | undefined;
      let invoiceXmlText: string | undefined;

      if (receiptFile) {
        receiptBase64 = await fileToBase64(receiptFile);
      }

      if (evidenceFile) {
        evidenceBase64 = await fileToBase64(evidenceFile);
      }

      if (invoicePdf) {
        invoicePdfBase64 = await fileToBase64(invoicePdf);
      }

      if (invoiceXml) {
        invoiceXmlText = await invoiceXml.text();
      }

      await createExpense({
        orderId: Number(orderId),
        bankId: Number(bankId),
        supplierId: Number(supplierId),
        productTypeId: Number(productTypeId),
        quantity: Number(quantity),
        unitPrice: Number(unitPrice),
        productId: Number(productId),
        amountSpent: parsedAmount,
        code: code.trim(),
        ...(orderDetailId ? { orderDetailId: Number(orderDetailId) } : {}),
        ...(invoicePdfBase64 ? { invoicePdf: invoicePdfBase64 } : {}),
        ...(receiptBase64 ? { receiptFile: receiptBase64 } : {}),
        ...(evidenceBase64 ? { evidenceFile: evidenceBase64 } : {}),
        ...(invoiceXmlText ? { invoiceXml: invoiceXmlText } : {}),
      });

      setMsg({
        t: "s",
        text: "✅ Gasto registrado con éxito",
      });

      clearFormButKeepOrder();
    } catch (err) {
      const flat = formatAxiosError(err);
      console.error("Error al registrar gasto:", flat);
      setMsg({
        t: "e",
        text: `❌ Error: ${flat.status ? `${flat.status} - ` : ""}${flat.message}`,
      });
    } finally {
      setLoading(false);
    }
  };

  const FileInputCard = ({
    title,
    accept,
    file,
    onChange,
    onRemove,
    helpText,
  }: {
    title: string;
    accept: string;
    file: File | null;
    onChange: (file: File | null) => void;
    onRemove: () => void;
    helpText: string;
  }) => {
    return (
      <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
        <label className="mb-2 block text-sm font-bold text-slate-700">
          {title}
        </label>

        <label className="flex min-h-[120px] cursor-pointer flex-col items-center justify-center rounded-xl border-2 border-dashed border-slate-300 bg-white px-4 py-5 text-center transition hover:border-indigo-400 hover:bg-indigo-50">
          <span className="text-sm font-semibold text-slate-700">
            {file ? "Cambiar archivo" : "Seleccionar archivo"}
          </span>
          <span className="mt-1 text-xs text-slate-500">{helpText}</span>
          <input
            type="file"
            accept={accept}
            className="hidden"
            onChange={(e) => onChange(e.target.files?.[0] ?? null)}
          />
        </label>

        {file ? (
          <div className="mt-3 rounded-xl border border-emerald-200 bg-emerald-50 p-3">
            <p className="break-all text-sm font-semibold text-emerald-800">
              {file.name}
            </p>
            <p className="mt-1 text-xs text-emerald-700">
              {(file.size / 1024 / 1024).toFixed(2)} MB
            </p>
            <button
              type="button"
              onClick={onRemove}
              className="mt-3 rounded-lg bg-rose-100 px-3 py-2 text-xs font-bold text-rose-700 transition hover:bg-rose-200"
            >
              Quitar archivo
            </button>
          </div>
        ) : (
          <p className="mt-3 text-xs text-slate-500">Ningún archivo seleccionado.</p>
        )}
      </div>
    );
  };

  return (
    <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8 lg:py-12">
      <header className="mb-8">
        <h1 className="text-2xl font-black text-white sm:text-3xl">
          Registrar Nuevo Gasto
        </h1>
        <p className="mt-2 text-sm text-indigo-200 sm:text-base">
          Asigna un ticket, factura o evidencia a una orden de compra
        </p>
      </header>

      <div className="grid grid-cols-1 gap-8 xl:grid-cols-3">
        <form
          onSubmit={handleSubmit}
          className="space-y-6 rounded-3xl bg-white p-5 shadow-xl sm:p-6 lg:col-span-2 lg:p-8"
        >
          <div>
            <label className="mb-1 block text-sm font-bold text-slate-700">
              Orden de Compra
            </label>
            <select
              value={orderId}
              onChange={(e) => setOrderId(e.target.value)}
              required
              disabled={loading || loadingCatalogs}
              className="w-full rounded-xl border border-slate-200 bg-slate-50 p-3 text-sm outline-none transition focus:ring-2 focus:ring-indigo-500 sm:text-base"
            >
              <option value="">-- Seleccionar Orden --</option>
              {orders.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.folio} - {o.customerName} ({o.statusName})
                </option>
              ))}
            </select>
          </div>

          {selectedOrder && (
            <div>
            {true && <button
              type="button"
              onClick={() => setIsOpen(true)}
              disabled={loading}
              className="w-full rounded-xl border border-slate-300 bg-white py-4 font-black uppercase tracking-widest text-slate-700 transition-all hover:bg-slate-50 disabled:opacity-50"
            >
              Escanear
            </button>}
              <label className="mb-1 block text-sm font-bold text-slate-700" style={{ marginTop: '20px' }}>
                Línea de OC
              </label>
              <select
                value={orderDetailId}
                onChange={(e) => setOrderDetailId(e.target.value)}
                disabled={loading}
                className="w-full rounded-xl border border-slate-200 bg-slate-50 p-3 text-sm outline-none transition focus:ring-2 focus:ring-indigo-500 sm:text-base"
              >
                <option value="">Sin línea (General)</option>
                {selectedOrder.items.map((it) => (
                  <option key={it.id} value={String(it.id)}>
                    #{it.id} - {it.nombre} (x{it.cantidad})
                  </option>
                ))}
              </select>

              <p className="mt-2 text-xs text-slate-500">
                Si seleccionas una línea, el gasto quedará ligado a esa partida de la OC.
              </p>
            </div>
          )}

          {!selectedOrder && (
            <div>
              <label className="mb-1 block text-sm font-bold text-slate-700">
                Categoría del gasto
              </label>
              <select
                value={purchaseCategoryId}
                onChange={(e) => setPurchaseCategoryId(e.target.value)}
                required
                disabled={loading || loadingCatalogs}
                className="w-full rounded-xl border border-slate-200 bg-slate-50 p-3 outline-none"
              >
                <option value="">-- Seleccionar Categoría del gasto --</option>
                {purchaseCategories.map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.name}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div>
              <label className="mb-1 block text-sm font-bold text-slate-700">
                Tipo de producto
              </label>
              <select
                value={productTypeId}
                onChange={(e) => setProductTypeId(e.target.value)}
                required
                disabled={loading || loadingCatalogs}
                className="w-full rounded-xl border border-slate-200 bg-slate-50 p-3 outline-none"
              >
                <option value="">-- Seleccionar Tipo de producto --</option>
                {productTypes.map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="mb-1 block text-sm font-bold text-slate-700">
                Producto
              </label>
              <select
                value={productId}
                onChange={(e) => setProductId(e.target.value)}
                required
                disabled={loading || loadingCatalogs}
                className="w-full rounded-xl border border-slate-200 bg-slate-50 p-3 outline-none"
              >
                <option value="">-- Seleccionar Producto --</option>
                {products.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div>
              <label className="mb-1 block text-sm font-bold text-slate-700">
                Cantidad
              </label>
              <input
                type="number"
                step="1"
                min="0"
                inputMode="numeric"
                value={quantity}
                onChange={(e) => {
                  setQuantity(e.target.value);
                  const amountSpent = Number(e.target.value) * Number(unitPrice);
                  setAmountSpent(amountSpent+'');
                }}
                placeholder="0"
                required
                disabled={loading}
                className="w-full rounded-xl border border-slate-200 bg-slate-50 p-3 text-lg font-bold text-indigo-600 outline-none"
              />
            </div>

            <div>
              <label className="mb-1 block text-sm font-bold text-slate-700">
                Precio unitario
              </label>
              <input
                type="number"
                step="0.01"
                min="0"
                inputMode="decimal"
                value={unitPrice}
                onChange={(e) => {
                  setUnitPrice(e.target.value);
                  const amountSpent = Number(quantity) * Number(e.target.value);
                  setAmountSpent(amountSpent+'');
                }}
                placeholder="0.00"
                required
                disabled={loading}
                className="w-full rounded-xl border border-slate-200 bg-slate-50 p-3 text-lg font-bold text-indigo-600 outline-none"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div>
              <label className="mb-1 block text-sm font-bold text-slate-700">
                Banco / Caja
              </label>
              <select
                value={bankId}
                onChange={(e) => setBankId(e.target.value)}
                required
                disabled={loading || loadingCatalogs}
                className="w-full rounded-xl border border-slate-200 bg-slate-50 p-3 outline-none"
              >
                <option value="">-- Seleccionar Banco --</option>
                {banks.map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.nombre}
                  </option>
                ))}
              </select>
            </div>
            
            <div>
              <label className="mb-1 block text-sm font-bold text-slate-700">
                Proveedor
              </label>
              <select
                value={supplierId}
                onChange={(e) => setSupplierId(e.target.value)}
                required
                disabled={loading || loadingCatalogs}
                className="w-full rounded-xl border border-slate-200 bg-slate-50 p-3 outline-none"
              >
                <option value="">-- Seleccionar Proveedor --</option>
                {suppliers.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.nombre}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div>
              <label className="mb-1 block text-sm font-bold text-slate-700">
                Monto del Gasto ($)
              </label>
              <input
                type="number"
                step="0.01"
                min="0"
                inputMode="decimal"
                value={amountSpent}
                onChange={(e) => setAmountSpent(e.target.value)}
                placeholder="0.00"
                required
                disabled={true}
                className="w-full rounded-xl border border-slate-200 bg-slate-50 p-3 text-lg font-bold text-indigo-600 outline-none"
              />
            </div>

            <div>
              <label className="mb-1 block text-sm font-bold text-slate-700">
                Número de Ticket / Folio
              </label>
              <input
                type="text"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                placeholder="Ej: FAC-12345"
                required
                disabled={loading}
                className="w-full rounded-xl border border-slate-200 bg-slate-50 p-3 outline-none"
              />
            </div>
          </div>

          <section className="space-y-4 rounded-2xl border border-slate-200 bg-slate-50/70 p-4 sm:p-5">
            <div>
              <h3 className="text-base font-black text-slate-800 sm:text-lg">
                Adjuntar comprobantes y evidencia
              </h3>
              <p className="mt-1 text-xs text-slate-500 sm:text-sm">
                Compatible con teléfono, tablet y escritorio. Puedes subir ticket, factura PDF/XML y evidencia adicional.
              </p>
            </div>

            <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
              <FileInputCard
                title="Ticket / Comprobante"
                accept=".pdf,.jpg,.jpeg,.png,.webp"
                file={receiptFile}
                onChange={setReceiptFile}
                onRemove={() => setReceiptFile(null)}
                helpText="Imagen. Máximo sugerido: 10 MB."
              />

              {false && (<FileInputCard
                title="Evidencia adicional"
                accept=".pdf,.jpg,.jpeg,.png,.webp"
                file={evidenceFile}
                onChange={setEvidenceFile}
                onRemove={() => setEvidenceFile(null)}
                helpText="Fotos o PDF relacionados con el gasto."
              />)}
              <div></div>

              <FileInputCard
                title="Factura PDF"
                accept=".pdf"
                file={invoicePdf}
                onChange={setInvoicePdf}
                onRemove={() => setInvoicePdf(null)}
                helpText="Solo PDF."
              />

              <FileInputCard
                title="Factura XML"
                accept=".xml"
                file={invoiceXml}
                onChange={setInvoiceXml}
                onRemove={() => setInvoiceXml(null)}
                helpText="Solo XML."
              />
            </div>
          </section>

          {msg && (
            <div
              className={`rounded-xl p-4 text-sm font-bold ${
                msg.t === "s"
                  ? "bg-emerald-100 text-emerald-700"
                  : "bg-rose-100 text-rose-700"
              }`}
            >
              {msg.text}
            </div>
          )}

          <BarcodeScannerModal
            isOpen={isOpen}
            onClose={() => setIsOpen(false)}
            onScan={setCodeBar}
          />

          <div className="flex flex-col gap-3 sm:flex-row">
            <button
              type="submit"
              disabled={loading || loadingCatalogs}
              className="w-full rounded-xl bg-indigo-600 py-4 font-black uppercase tracking-widest text-white shadow-lg transition-all hover:bg-indigo-700 disabled:opacity-50"
            >
              {loading ? "Registrando..." : "🚀 Guardar Gasto"}
            </button>

            <button
              type="button"
              onClick={clearFormButKeepOrder}
              disabled={loading}
              className="w-full rounded-xl border border-slate-300 bg-white py-4 font-black uppercase tracking-widest text-slate-700 transition-all hover:bg-slate-50 disabled:opacity-50"
            >
              Limpiar
            </button>
          </div>
        </form>

        <aside className="space-y-4">
          <div className="rounded-2xl border border-white/10 bg-slate-900/40 p-5 text-white backdrop-blur-md sm:p-6">
            <h4 className="mb-4 text-xs font-black uppercase tracking-tighter text-indigo-300">
              Resumen de Orden
            </h4>

            {selectedOrder ? (
              <div className="space-y-4">
                <div>
                  <p className="text-[10px] uppercase opacity-60">Presupuesto Inicial</p>
                  <p className="text-lg font-bold sm:text-xl">
                    {selectedOrder.presupuestoTotal.toLocaleString("es-MX", {
                      style: "currency",
                      currency: "MXN",
                    })}
                  </p>
                </div>

                <div>
                  <p className="text-[10px] uppercase opacity-60">Gastado Acumulado</p>
                  <p className="text-lg font-bold text-rose-300 sm:text-xl">
                    {selectedOrder.totalEstimado.toLocaleString("es-MX", {
                      style: "currency",
                      currency: "MXN",
                    })}
                  </p>
                </div>

                <div className="border-t border-white/10 pt-4">
                  <p className="text-[10px] uppercase opacity-60">Disponible Actual</p>
                  <p
                    className={`text-xl font-black sm:text-2xl ${
                      selectedOrder.presupuestoTotal - selectedOrder.totalEstimado < 0
                        ? "text-rose-500"
                        : "text-emerald-400"
                    }`}
                  >
                    {(selectedOrder.presupuestoTotal - selectedOrder.totalEstimado).toLocaleString(
                      "es-MX",
                      {
                        style: "currency",
                        currency: "MXN",
                      }
                    )}
                  </p>
                </div>

                <div className="border-t border-white/10 pt-4">
                  <p className="text-[10px] uppercase opacity-60">
                    Disponible después de este gasto
                  </p>
                  <p
                    className={`text-lg font-black sm:text-xl ${
                      (remainingAfterExpense ?? 0) < 0
                        ? "text-rose-400"
                        : "text-cyan-300"
                    }`}
                  >
                    {remainingAfterExpense !== null
                      ? remainingAfterExpense.toLocaleString("es-MX", {
                          style: "currency",
                          currency: "MXN",
                        })
                      : "—"}
                  </p>
                </div>
              </div>
            ) : (
              <p className="text-sm italic text-slate-400">
                Selecciona una orden para ver el presupuesto disponible.
              </p>
            )}
          </div>

          <div className="rounded-2xl border border-white/10 bg-slate-900/40 p-5 text-white backdrop-blur-md">
            <h4 className="mb-3 text-xs font-black uppercase tracking-tighter text-indigo-300">
              Recomendaciones
            </h4>
            <div className="space-y-2 text-sm text-slate-200">
              <p>• Sube ticket o comprobante cuando exista.</p>
              <p>• Si hay factura, adjunta PDF y XML.</p>
              <p>• Usa evidencia adicional para fotos o respaldo.</p>
              <p>• Mantén archivos con nombres claros y de tamaño moderado.</p>
            </div>
          </div>

          <button
            type="button"
            onClick={() => navigate("/admin/odes")}
            className="w-full rounded-xl border border-white/20 py-3 text-xs font-bold text-white transition-all hover:bg-white/10"
          >
            📋 Volver al Listado
          </button>
        </aside>
      </div>
    </div>
  );
};

export default GastosPage;
